package com.schoolconnect.app.util

import korlibs.image.bitmap.Bitmap
import korlibs.image.bitmap.resized
import korlibs.image.format.ImageEncodingProps
import korlibs.image.format.JPEG
import korlibs.image.format.readBitmap
import korlibs.io.file.VfsFile
import korlibs.io.file.std.MemoryVfs
import korlibs.math.geom.Anchor
import korlibs.math.geom.ScaleMode
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlin.math.sqrt
import kotlin.time.Clock

private const val MIN_RESIZE_DIMENSION = 64
private const val MAX_RESIZE_PASSES = 4
private const val SCALE_BACKOFF = 0.85
private const val INITIAL_JPEG_QUALITY = 0.95
private const val MIN_JPEG_QUALITY = 0.55
private const val QUALITY_BACKOFF = 0.88
private const val IMAGE_PIPELINE_TAG = "IMG_PIPELINE"
private const val MIN_SKIP_COMPRESS_BYTES = 100_000
private const val MAX_SKIP_COMPRESS_BYTES = 500_000

private fun Bitmap.encodeAsJpeg(quality: Double = INITIAL_JPEG_QUALITY): ByteArray {
    return JPEG.encode(this, ImageEncodingProps(quality = quality))
}

fun isImageLessThan1MB(imageData: ByteArray): Boolean {
    val maxSizeInBytes = 1024 * 1024
    return imageData.size < maxSizeInBytes
}

fun sanitizeFilename(filename: String): String {
    val lastDotIndex = filename.lastIndexOf('.')

    return if (lastDotIndex != -1) {
        val namePart = filename.substring(0, lastDotIndex).replace("[^a-zA-Z0-9]".toRegex(), "")
        val extension = filename.substring(lastDotIndex)
        "$namePart$extension"
    } else {
        filename.replace("[^a-zA-Z0-9]".toRegex(), "")
    }
}

suspend fun resizeImage(byteArray: ByteArray, targetSizeBytes: Long = 500_000): ByteArray =
    withContext(Dispatchers.Default) {
        if (byteArray.size in MIN_SKIP_COMPRESS_BYTES..MAX_SKIP_COMPRESS_BYTES) {
            scLog("$IMAGE_PIPELINE_TAG resize skip in_range bytes=${byteArray.size}")
            byteArray
        } else if (byteArray.size <= targetSizeBytes) {
            byteArray
        } else {
            val startMs = Clock.System.now().toEpochMilliseconds()
            scLog("$IMAGE_PIPELINE_TAG resize start input_bytes=${byteArray.size} target_bytes=$targetSizeBytes")
            val originalBitmap = getVfsFile(byteArray).readBitmap().toBMP32IfRequired()
            val result = originalBitmap.compressToTargetBytes(byteArray.size, targetSizeBytes)
            val elapsedMs = Clock.System.now().toEpochMilliseconds() - startMs
            scLog("$IMAGE_PIPELINE_TAG resize done output_bytes=${result.size} elapsed_ms=$elapsedMs w=${originalBitmap.width} h=${originalBitmap.height}")
            result
        }
    }

fun Bitmap.compressToTargetBytes(sourceByteCount: Int, targetSizeBytes: Long): ByteArray {
    val startMs = Clock.System.now().toEpochMilliseconds()
    scLog("$IMAGE_PIPELINE_TAG compress init source_bytes=$sourceByteCount target_bytes=$targetSizeBytes w=$width h=$height")
    val originalWidth = width
    val originalHeight = height
    val initialRatio = sqrt(targetSizeBytes.toDouble() / sourceByteCount.toDouble()).coerceAtMost(1.0)

    var targetWidth = maxOf((originalWidth * initialRatio).toInt(), MIN_RESIZE_DIMENSION)
    var targetHeight = maxOf((originalHeight * initialRatio).toInt(), MIN_RESIZE_DIMENSION)

    var resizedBitmap = resized(targetWidth, targetHeight, ScaleMode.FIT, Anchor.CENTER, false)
    var quality = INITIAL_JPEG_QUALITY
    var encodedBytes = resizedBitmap.encodeAsJpeg(quality)

    scLog("$IMAGE_PIPELINE_TAG compress pass=0 size=${encodedBytes.size} w=$targetWidth h=$targetHeight q=$quality")
    if (encodedBytes.size <= targetSizeBytes) return encodedBytes

    repeat(MAX_RESIZE_PASSES) {
        while (encodedBytes.size > targetSizeBytes && quality > MIN_JPEG_QUALITY) {
            quality = (quality * QUALITY_BACKOFF).coerceAtLeast(MIN_JPEG_QUALITY)
            encodedBytes = resizedBitmap.encodeAsJpeg(quality)
            scLog("$IMAGE_PIPELINE_TAG compress q_pass size=${encodedBytes.size} w=$targetWidth h=$targetHeight q=$quality")
            if (encodedBytes.size <= targetSizeBytes) {
                val elapsedMs = Clock.System.now().toEpochMilliseconds() - startMs
                scLog("$IMAGE_PIPELINE_TAG compress done output_bytes=${encodedBytes.size} elapsed_ms=$elapsedMs")
                return encodedBytes
            }
        }

        targetWidth = maxOf((targetWidth * SCALE_BACKOFF).toInt(), MIN_RESIZE_DIMENSION)
        targetHeight = maxOf((targetHeight * SCALE_BACKOFF).toInt(), MIN_RESIZE_DIMENSION)
        quality = INITIAL_JPEG_QUALITY

        resizedBitmap = resized(targetWidth, targetHeight, ScaleMode.FIT, Anchor.CENTER, false)
        encodedBytes = resizedBitmap.encodeAsJpeg(quality)
        scLog("$IMAGE_PIPELINE_TAG compress pass=${it + 1} size=${encodedBytes.size} w=$targetWidth h=$targetHeight q=$quality")

        if (encodedBytes.size <= targetSizeBytes ||
            (targetWidth == MIN_RESIZE_DIMENSION && targetHeight == MIN_RESIZE_DIMENSION)
        ) {
            val elapsedMs = Clock.System.now().toEpochMilliseconds() - startMs
            scLog("$IMAGE_PIPELINE_TAG compress done output_bytes=${encodedBytes.size} elapsed_ms=$elapsedMs")
            return encodedBytes
        }
    }

    val elapsedMs = Clock.System.now().toEpochMilliseconds() - startMs
    scLog("$IMAGE_PIPELINE_TAG compress done output_bytes=${encodedBytes.size} elapsed_ms=$elapsedMs")
    return encodedBytes
}

suspend fun rotateLeft(byteArray: ByteArray): ByteArray {
    val vfsFile = getVfsFile(byteArray)
    val bitmap = vfsFile.readBitmap()
    val rotatedBitmap = bitmap.rotatedLeft()
    return rotatedBitmap.encodeAsJpeg()
}

suspend fun rotateRight(byteArray: ByteArray): ByteArray {
    val vfsFile = getVfsFile(byteArray)
    val bitmap = vfsFile.readBitmap()
    val rotatedBitmap = bitmap.rotatedRight()
    return rotatedBitmap.encodeAsJpeg()
}

suspend fun getImageSize(vfsFile: VfsFile): Pair<Int, Int> {
    val bitmap: Bitmap = vfsFile.readBitmap()
    return Pair(bitmap.width, bitmap.height)
}

suspend fun getVfsFile(byteArray: ByteArray): VfsFile {
    return MemoryVfs()["tempImage"].apply { write(byteArray) }
}
